import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { VALIDATION_GATE_QUERY_KEY } from '@/features/validation-gate/constants';
import { isOfflineDbAvailable } from './offline-db';
import { flushOutbox, type FlushResult, type FlushStop } from './outbox-flush';
import { listenToOtherTabs, OUTBOX_CHANGED_EVENT, outboxStore } from './outbox-store';
import { sendOutboxItem } from './outbox-senders';
import { OfflineSyncContext, type OfflineSyncValue } from './offline-sync-context';
import type { OutboxItem } from './outbox.types';

/** How often the queue is looked at while something waits. */
const TICK_MS = 20_000;
const RETRY_OFFLINE_MS = 30_000;
const RETRY_IN_PROGRESS_MS = 30_000;
const RETRY_GATE_MS = 10 * 60_000;
const SERVER_BACKOFF_MIN_MS = 30_000;
const SERVER_BACKOFF_MAX_MS = 10 * 60_000;
const LOCK_NAME = 'yodi-outbox-flush';

const readToken = () => {
  try {
    return localStorage.getItem('yodi-token') ?? '';
  } catch {
    return '';
  }
};

/** One flush at a time across tabs; null when another tab is already flushing. */
async function withFlushLock(run: () => Promise<FlushResult>): Promise<FlushResult | null> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return run();
  return locks.request(LOCK_NAME, { ifAvailable: true }, async (lock) => (lock ? run() : null));
}

/**
 * Sends the signed-in account's queued contributions: at start, when the
 * network comes back, when the tab comes back to the front, every 20 s while
 * something waits (with a backoff after a server error or the cross-validation
 * gate), and on demand. After a 401 it waits for a new sign-in (another token)
 * instead of retrying with the rejected one.
 */
export function OfflineSyncProvider({ children }: { children: ReactNode }) {
  const { user, t } = useApp();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const ownerId = user?.id != null ? String(user.id) : null;

  const [isOnline, setIsOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  const [items, setItems] = useState<OutboxItem[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastStop, setLastStop] = useState<FlushStop | null>(null);

  const flushingRef = useRef(false);
  const nextAttemptAtRef = useRef(0);
  const serverBackoffRef = useRef(0);
  /** The token a 401 refused: no automatic flush until it changes. */
  const refusedTokenRef = useRef<string | null>(null);
  const pendingRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!ownerId || !isOfflineDbAvailable()) {
      setItems([]);
      return;
    }
    try {
      setItems(await outboxStore.list(ownerId));
    } catch {
      setItems([]);
    }
  }, [ownerId]);

  useEffect(() => {
    void refresh();
    listenToOtherTabs();
    const onChange = () => void refresh();
    window.addEventListener(OUTBOX_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(OUTBOX_CHANGED_EVENT, onChange);
  }, [refresh]);

  const pendingCount = items.filter((item) => item.status === 'pending').length;
  pendingRef.current = pendingCount;

  const schedule = useCallback((result: FlushResult) => {
    const now = Date.now();
    serverBackoffRef.current = result.stoppedBy === 'server'
      ? Math.min(SERVER_BACKOFF_MAX_MS, Math.max(SERVER_BACKOFF_MIN_MS, serverBackoffRef.current * 2))
      : 0;
    refusedTokenRef.current = result.stoppedBy === 'auth' ? readToken() : null;
    if (result.stoppedBy === 'server') nextAttemptAtRef.current = now + serverBackoffRef.current;
    else if (result.stoppedBy === 'gate') nextAttemptAtRef.current = now + RETRY_GATE_MS;
    else if (result.stoppedBy === 'offline') nextAttemptAtRef.current = now + RETRY_OFFLINE_MS;
    else if (result.inProgress > 0) nextAttemptAtRef.current = now + RETRY_IN_PROGRESS_MS;
    else nextAttemptAtRef.current = 0;
  }, []);

  const flush = useCallback(
    async (manual: boolean) => {
      if (!ownerId || flushingRef.current || !isOfflineDbAvailable()) return;
      if (navigator.onLine === false) {
        setLastStop('offline');
        return;
      }
      if (!manual) {
        if (Date.now() < nextAttemptAtRef.current) return;
        if (refusedTokenRef.current !== null && refusedTokenRef.current === readToken()) return;
      }

      flushingRef.current = true;
      setIsSyncing(true);
      try {
        const result = await withFlushLock(() => flushOutbox({ ownerId, send: sendOutboxItem }));
        if (!result) return;
        setLastStop(result.stoppedBy);
        schedule(result);
        if (result.sent > 0 || result.stoppedBy === 'gate') {
          void queryClient.invalidateQueries({ queryKey: VALIDATION_GATE_QUERY_KEY });
        }
        if (result.sent > 0) {
          toast({ title: t.offline.syncedTitle, description: t.offline.syncedDesc.replace('{count}', String(result.sent)) });
        }
      } catch (error) {
        console.debug('Offline outbox flush failed', error);
      } finally {
        flushingRef.current = false;
        setIsSyncing(false);
        void refresh();
      }
    },
    [ownerId, queryClient, refresh, schedule, t, toast],
  );

  // A new account (or a sign-in after a 401) starts with a clean slate.
  useEffect(() => {
    nextAttemptAtRef.current = 0;
    serverBackoffRef.current = 0;
    refusedTokenRef.current = null;
    setLastStop(null);
    if (ownerId) void flush(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      nextAttemptAtRef.current = 0;
      void flush(false);
    };
    const onOffline = () => setIsOnline(false);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && pendingRef.current > 0) void flush(false);
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (pendingRef.current > 0) void flush(false);
    }, TICK_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [flush]);

  const retryItem = useCallback(
    async (id: string) => {
      if (!ownerId) return;
      await outboxStore.retry(ownerId, id);
      void flush(true);
    },
    [flush, ownerId],
  );

  const deleteItem = useCallback(
    async (id: string) => {
      if (!ownerId) return;
      await outboxStore.remove(ownerId, id);
    },
    [ownerId],
  );

  const value = useMemo<OfflineSyncValue>(
    () => ({
      isOnline,
      pendingCount,
      failedItems: items.filter((item) => item.status === 'failed'),
      isSyncing,
      lastStop,
      syncNow: () => void flush(true),
      retryItem,
      deleteItem,
    }),
    [deleteItem, flush, isOnline, isSyncing, items, lastStop, pendingCount, retryItem],
  );

  return <OfflineSyncContext.Provider value={value}>{children}</OfflineSyncContext.Provider>;
}
